import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../Modal/Modal';
import { Search, UserX } from 'lucide-react';
import customerService from '../../services/customer.service';
import { CustomerListItemV2 } from '../../types/customer.types';
import CustomersPagination from '../Customers/Pagination';
import './TicketLinkCustomerModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (customer: { id: number; full_name?: string | null; identification_no?: string | null; phone?: string | null; email?: string | null }) => void;
  /** Начальный поисковый запрос (например, телефон или email для предзаполнения) */
  initialSearch?: string;
}

const pageSizeOptions = [10, 20, 50] as const;

/**
 * Модальное окно поиска и привязки клиента к тикету
 */
const TicketLinkCustomerModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, initialSearch }) => {
  // Состояние поиска
  const [search, setSearch] = useState('');

  // Состояние пагинации
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<typeof pageSizeOptions[number]>(20);

  // Состояние данных
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<CustomerListItemV2[]>([]);
  const [total, setTotal] = useState(0);

  // Состояние выбора
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const skip = useMemo(() => (page - 1) * pageSize, [page, pageSize]);

  /**
   * Загрузка клиентов
   */
  const load = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await customerService.getCustomers({ skip, limit: pageSize, search: search.trim() });
      setItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить клиентов');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen, skip, pageSize, search]);

  // Сброс страницы при изменении поиска или размера страницы
  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  // Загрузка при изменении зависимостей
  useEffect(() => {
    void load();
  }, [load]);

  // Сброс при закрытии / применение начального поиска при открытии
  useEffect(() => {
    if (isOpen) {
      // При открытии применяем начальный поиск, если есть
      if (initialSearch) {
        setSearch(initialSearch);
      }
    } else {
      // При закрытии сбрасываем всё
      setSearch('');
      setPage(1);
      setPageSize(20);
      setItems([]);
      setSelectedId(null);
      setError(null);
    }
  }, [isOpen, initialSearch]);

  /**
   * Сброс поиска
   */
  const handleResetSearch = () => {
    setSearch('');
  };

  /**
   * Поиск
   */
  const handleSearch = () => {
    setPage(1);
    void load();
  };

  const getName = (c: CustomerListItemV2) => {
    const full = (c.full_name || '').trim();
    const byParts = `${c.private_last_name ?? ''} ${c.private_first_name ?? ''}`.trim();
    return full || byParts || '—';
  };

  const getPrimaryPhone = (c: CustomerListItemV2) => {
    const p = Array.isArray(c.phones) ? (c.phones[0]?.number || '') : '';
    return p || '—';
  };

  const getPrimaryEmail = (c: CustomerListItemV2) => {
    const e = Array.isArray(c.emails) ? (c.emails[0]?.email || '') : '';
    return e || '—';
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canGoPrev = page > 1;

  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  /**
   * Обработка привязки
   */
  const handleSubmit = () => {
    if (!selected) return;
    onSubmit({
      id: selected.id,
      full_name: selected.full_name,
      identification_no: selected.identification_no ?? null,
      phone: (Array.isArray(selected.phones) && selected.phones[0]?.number) || null,
      email: (Array.isArray(selected.emails) && selected.emails[0]?.email) || null,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Связать с клиентом" size="xl">
      <div className="link-customer-modal">
        {/* Секция поиска */}
        <div className="link-customer-modal__search-section">
          <div className="link-customer-modal__search-row">
            <div className="link-customer-modal__search-input-wrapper">
              <Search size={18} className="link-customer-modal__search-icon" />
              <input
                type="text"
                className="input link-customer-modal__search-input"
                placeholder="Поиск по ФИО, ИИН, телефону или email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <div className="link-customer-modal__search-actions">
              {search && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleResetSearch}
                >
                  Сбросить
                </button>
              )}
              <button
                type="button"
                className="btn-primary"
                onClick={handleSearch}
              >
                Найти
              </button>
            </div>
          </div>
        </div>

        {/* Информация о результатах */}
        <div className="link-customer-modal__results-info">
          <div className="text-muted text-sm">
            {total > 0
              ? `Показано ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} из ${total}`
              : 'Нет результатов'}
          </div>
          <div className="flex gap-8px items-center">
            <label className="text-muted text-sm">На странице</label>
            <select
              className="input w-80px"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value) as typeof pageSizeOptions[number]); setPage(1); }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ошибка */}
        {error && (
          <div className="error-container">
            <div className="error-message">
              <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3>Произошла ошибка</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Таблица клиентов */}
        <div className="table-container link-customer-modal__table-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Загрузка клиентов...</p>
            </div>
          ) : (
            <table className="data-table data-table--minimal">
              <thead>
                <tr>
                  <th style={{ width: 44 }}></th>
                  <th>ФИО</th>
                  <th>ИИН</th>
                  <th>Телефон</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty-state" style={{ padding: 24 }}>
                        <UserX size={48} color="var(--gray-400)" />
                        <h3>Клиенты не найдены</h3>
                        <p>Измените параметры поиска</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedId(c.id)}
                      className={`${selectedId === c.id ? 'selected' : ''} cursor-pointer`}
                    >
                      <td>
                        <input
                          type="radio"
                          name="selectedCustomer"
                          checked={selectedId === c.id}
                          onChange={() => setSelectedId(c.id)}
                        />
                      </td>
                      <td className="link-customer-modal__name-cell">
                        {getName(c)}
                      </td>
                      <td>{c.identification_no || '—'}</td>
                      <td>{getPrimaryPhone(c)}</td>
                      <td>{getPrimaryEmail(c)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Пагинация */}
        <CustomersPagination
          meta={{
            current_page: page,
            page_size: pageSize,
            has_previous: canGoPrev,
            has_next: page < totalPages,
            total_items: total,
            total_pages: totalPages,
          }}
          onPageChange={(p) => setPage(p)}
        />

        {/* Кнопки действий */}
        <div className="link-customer-modal__actions">
          <button className="btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn-primary"
            disabled={!selected}
            onClick={handleSubmit}
          >
            Привязать
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TicketLinkCustomerModal;
